// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, useCallback } from 'react';

import { type HexTriplet, FLAT_BLACK_FILLER } from '@evolvemep/api-ts';

import { createColorScheme } from 'helpers/createColorScheme';

export type ColorSchemeOptions<S, T extends string | number | symbol> = {
  getIdentifier: (spool: S) => T;
  colorSchemeArgs?: [startHue: number] | [startHue: number, saturation: number];
};

/**
 * Given a list of spools, creates a color scheme.
 * @param getIdentifier - Function that returns a unique identifier for each spool. This is used in an effect, so it should be constant or memoized
 * @param colorSchemeArgs - Function that returns a unique identifier for each spool. This is used in an effect, so it should be constant or memoized
 */
export const useSpoolColorScheme = <S, T extends string | number | symbol>({
  getIdentifier,
  colorSchemeArgs,
}: ColorSchemeOptions<S, T>) => {
  const [colorScheme, setColorScheme] = useState<Record<T, HexTriplet>>({} as Record<T, HexTriplet>);
  const initializeColorScheme = useCallback(
    (spools: readonly S[] | null) => {
      if (spools === null) {
        setColorScheme({} as Record<T, HexTriplet>);
        return;
      }
      const newColorScheme = createColorScheme(spools.length, ...(colorSchemeArgs ?? []));
      setColorScheme(
        spools.reduce(
          (acc, curr, i) => ({ ...acc, [getIdentifier(curr)]: newColorScheme[i] ?? FLAT_BLACK_FILLER }),
          {} as Record<T, HexTriplet>,
        ),
      );
    },
    [getIdentifier, colorSchemeArgs],
  );
  const updateColor = useCallback((identifier: T, newColor: HexTriplet) => {
    setColorScheme((c) => ({ ...c, [identifier]: newColor }));
  }, []);

  return { colorScheme, initializeColorScheme, updateColor, setColorScheme } as const;
};
